import React from 'react';
import { motion } from 'framer-motion';

type EmptyType = 'no-data' | 'no-sites' | 'error' | 'filtered-empty';

interface EmptyAnalyticsProps {
    type?: EmptyType;
    onReset?: () => void;
    onRetry?: () => void;
    errorMessage?: string;
}

interface EmptyConfig {
    icon: React.ReactNode;
    title: string;
    desc: string;
    accent: string;
    pulsing: boolean;
}

const CONFIGS: Record<EmptyType, EmptyConfig> = {
    'no-data': {
        icon: (
            <svg width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <path d="M3 9h18M9 21V9"/>
                <circle cx="15" cy="15" r="2" strokeWidth="1.5"/>
            </svg>
        ),
        title: 'Данных пока нет',
        desc: 'Как только будет собрана статистика за выбранный период — она появится здесь.',
        accent: 'var(--brand)',
        pulsing: false,
    },
    'no-sites': {
        icon: (
            <svg width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10"/>
                <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
        ),
        title: 'Нет сайтов для анализа',
        desc: 'Добавьте сайты в мониторинг, чтобы начать получать аналитику.',
        accent: 'var(--status-warn)',
        pulsing: true,
    },
    'error': {
        icon: (
            <svg width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
        ),
        title: 'Ошибка загрузки',
        desc: 'Не удалось получить аналитические данные. Проверьте соединение и попробуйте снова.',
        accent: 'var(--status-down)',
        pulsing: true,
    },
    'filtered-empty': {
        icon: (
            <svg width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                <line x1="3" y1="3" x2="21" y2="21"/>
            </svg>
        ),
        title: 'Нет данных по фильтру',
        desc: 'Попробуйте изменить фильтры или выбрать другой период.',
        accent: 'var(--text-muted)',
        pulsing: false,
    },
};

export const EmptyAnalytics: React.FC<EmptyAnalyticsProps> = ({
    type = 'no-data',
    onReset,
    onRetry,
    errorMessage,
}) => {
    const config = CONFIGS[type];

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
            className="flex flex-col items-center justify-center py-16 px-6 text-center"
        >
            <motion.div
                initial={{ scale: 0.75, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.08, type: 'spring', stiffness: 260, damping: 22 }}
                className="w-20 h-20 rounded-2xl flex items-center justify-center mb-5 relative"
                style={{
                    background: `color-mix(in srgb, ${config.accent} 10%, transparent)`,
                    color: config.accent,
                    border: `1px solid color-mix(in srgb, ${config.accent} 18%, transparent)`,
                }}
            >
                {config.pulsing && (
                    <span
                        className="absolute inset-0 rounded-2xl animate-ping opacity-15"
                        style={{ background: config.accent }}
                    />
                )}
                {config.icon}
            </motion.div>

            <motion.h3
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.14 }}
                className="text-[16px] font-semibold text-[var(--text)] mb-2"
            >
                {config.title}
            </motion.h3>

            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-[13px] text-[var(--text-secondary)] max-w-[280px] leading-relaxed mb-7"
            >
                {errorMessage ?? config.desc}
            </motion.p>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.26 }}
                className="flex items-center gap-2"
            >
                {onRetry && (
                    <button
                        onClick={onRetry}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[var(--brand)] text-white text-[12px] font-medium hover:opacity-90 active:opacity-80 transition-opacity"
                    >
                        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <polyline points="23 4 23 10 17 10"/>
                            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                        </svg>
                        Попробовать снова
                    </button>
                )}

                {onReset && (
                    <button
                        onClick={onReset}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[var(--border)] text-[var(--text-secondary)] text-[12px] font-medium hover:bg-[var(--bg-hover)] hover:text-[var(--text)] transition-all"
                    >
                        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                        Сбросить фильтры
                    </button>
                )}
            </motion.div>
        </motion.div>
    );
};